import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSourceVersions } from '@/hooks/lesson/useSourceVersions';
import { deriveLockState } from '@/lib/sources/lockState';
import { Button, TabbedInterface, EmptyState } from '@ai-tutor/ui';
import { SourceItem } from './SourceItem';
import { StagedActionBar } from './StagedActionBar';
import { PromptOverviewPanel } from '../content/prompt/PromptOverviewPanel';
import { PromptOverviewSkeleton } from '../skeletons/PanelSkeletons';
import { VersionSelector } from '../context/VersionSelector';
import { SourcesPanelCollapsed } from './SourcesPanelCollapsed';
import { SourceIngestBanner } from './SourceIngestBanner';
import { SourcesIngestingView } from './SourcesIngestingView';
import { PastVersionNotice } from './PastVersionNotice';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { PanelFallback } from '@/components/core/common/feedback/PanelFallback';
import { useLessonTabParam } from '@/hooks/lesson/useLessonTabParam';
import {
  AttachSourceModal,
  InLessonAttachSourceModal,
  type StagedSource,
} from '@/components/features/sources/attach';
import {
  Plus,
  FolderOpen,
  Brain,
  History,
  ChevronDown,
  ArrowLeft,
  X,
  Loader2,
  Sidebar,
} from 'lucide-react';
import { mockSources, type Source } from '@/data/fixtures';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  useSourcesList,
  useDeleteSource,
  useToggleStagedSource,
  useRegenerateLesson,
} from '@/hooks/lesson/useSources';
import type {
  LessonMetadata,
  SourceItem as ApiSourceItem,
} from '@/lib/api-client';
import type { StepView, SourcesView } from '@/types/pipeline';
import { progressApi } from '@/lib/api-client';

export interface SourcesPanelProps {
  lessonId?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  currentVersion?: string;
  onVersionChange?: (version: string) => void;
  onSourceSelect?: (source: Source | ApiSourceItem) => void;
  /**
   * Lesson metadata threaded down from `LessonLearningLayout`. Required
   * by the Context tab (PromptOverviewPanel) - when absent, the tab
   * renders its own skeleton state until metadata loads.
   */
  lessonMetadata?: LessonMetadata;
  /**
   * Per-step view map from the page-level `usePipelineStatus` poll.
   * PromptOverviewPanel reads `overview` and `byte_selection` state out of
   * this; other panels consume different keys.
   */
  steps?: Record<string, StepView>;
  /** Source-ingestion rollup - drives the "still ingesting" view. */
  sources?: SourcesView;
  /** True once the lesson's pipeline is terminal. Until then the panel is
   *  read-only (no prompt edits, no add/remove sources, no new version). */
  lessonDone?: boolean;
  /**
   * Set when the panel is shown in a sheet (phone / tablet). The header then
   * swaps its collapse toggle for a close button and drops its back arrow -
   * the page's own top bar already has one.
   */
  onClose?: () => void;
}

const PANEL_TABS = ['sources', 'context'] as const;

export function SourcesPanel({
  lessonId,
  isCollapsed = false,
  onToggleCollapse,
  currentVersion = 'v1.0 - Initial',
  onVersionChange,
  onSourceSelect,
  lessonMetadata,
  steps,
  sources: sourcesView,
  lessonDone,
  onClose,
}: SourcesPanelProps) {
  const navigate = useNavigate();
  const { isMock, isApi } = useDataMode();
  // Mock mode: local state; API mode: use hooks
  const [mockSourceList, setMockSourceList] = useState<Source[]>(
    isMock ? mockSources : []
  );
  // `?panel=` — see `useLessonTabParam`. Prompt (`context`) by default (#98).
  const [activeTab, setActiveTab] = useLessonTabParam(
    'panel',
    PANEL_TABS,
    'context'
  );
  const [attachOpen, setAttachOpen] = useState(false);
  const [showVersionSelector, setShowVersionSelector] = useState(false);

  // API mode hooks
  const {
    sources: apiSources,
    isLoading: apiLoading,
    error: apiError,
    refetch: refetchSources,
  } = useSourcesList(isApi ? lessonId : undefined);
  const deleteSourceMutation = useDeleteSource(lessonId || '');
  const toggleStaged = useToggleStagedSource(lessonId || '');
  const regenerate = useRegenerateLesson(lessonId || '');
  const stagingDisabled = regenerate.isPending;

  // Version history lives in `useSourceVersions`. Its two React Query calls
  // stay first and unconditional in that hook, in the same order they had
  // here, so hook order is unchanged.
  const {
    apiVersions,
    currentVersionRecord,
    apiCurrentVersion,
    latestVersion,
    isViewingLatest,
    displayVersion,
    versionTriggerDisabled,
    isSwitching,
    handleVersionChange,
  } = useSourceVersions({
    isApi,
    lessonId,
    currentVersion,
    onVersionChange,
    onSwitched: () => setShowVersionSelector(false),
  });

  // While the lesson is still generating (pipeline not terminal) the panel is
  // read-only: no prompt edits, no add/remove sources, no new version. Editing
  // resumes once the pipeline completes. `lessonDone` only flows in API mode;
  // mock mode is always editable.
  //
  // Read-only rules live in `lib/sources/lockState` — a pure function, so it
  // is unit-tested there rather than only exercised through this 780-line
  // component. See that file for why each rule exists.
  const { pipelineLocked, locked, sourcesIngesting, sourcesAllFailed } =
    deriveLockState({
      isApi,
      steps,
      lessonDone,
      isViewingLatest,
      hasUserSources: lessonMetadata?.has_user_sources,
      sourcesView,
    });
  // A past version wins: switching is its fix even if the latest is building.
  const lockReason = !isViewingLatest ? 'past-version' : 'building';
  // Synthetic sources (backend-generated when the user provides no
  // sources of their own) are excluded from `/lessons/{id}/sources` by
  // the backend (`Source.is_internal == False` filter). But their IDs
  // still appear in `staged_source_ids` and the version's `source_ids`
  // because they're real records in the lesson. To keep the UI honest
  // we restrict every source-id set we render against to the IDs the
  // user can actually see - synthetics are then invisible end-to-end:
  //   * No phantom checkbox state on the source list (none anyway,
  //     since their items are filtered out upstream).
  //   * No "+N added" phantom diff in `StagedActionBar` caused by
  //     synthetic IDs being staged but not in the visible source list.
  const knownSourceIds = new Set<string>(
    isApi ? (apiSources as ApiSourceItem[]).map(s => s.source_id) : []
  );
  // Default the checkbox state to the *current version's* source_ids when the
  // backend has not yet materialized a staged set. A null/undefined
  // `staged_source_ids` means "user hasn't edited" - so the visible "what
  // will be in the next version" is identical to the current version. Only
  // an explicit empty array means "user unchecked everything".
  const rawStagedIds =
    lessonMetadata?.staged_source_ids ?? currentVersionRecord?.source_ids ?? [];
  const stagedSet = new Set<string>(
    isApi ? rawStagedIds.filter(id => knownSourceIds.has(id)) : []
  );
  // Unified sources list
  const sources: (Source | ApiSourceItem)[] = isApi
    ? apiSources
    : mockSourceList;

  // View-based filtering. On the latest version the visible pool is just the
  // active sources (soft-deleted ones are hidden - they're not part of the
  // editable working set). On a past version the visible list is whatever
  // that version actually referenced - including soft-deleted entries - so
  // the user can see what the version was made of, with the Deleted badge
  // rendering the difference. Mock sources have no `source_id`/`is_active`
  // and fall through unchanged.
  const visibleSources: (Source | ApiSourceItem)[] = isApi
    ? isViewingLatest
      ? sources.filter(s => !('source_id' in s) || s.is_active)
      : sources.filter(
          s =>
            !('source_id' in s) ||
            (currentVersionRecord?.source_ids ?? []).includes(s.source_id)
        )
    : sources;

  /**
   * Mock-mode adapter: maps a StagedSource into the local mock Source shape
   * so the panel still works without a backend. Used only when !isApi.
   */
  const handleMockAdd = (s: StagedSource) => {
    const base: Omit<Source, 'id' | 'uploadedAt'> =
      s.kind === 'file'
        ? {
            name: s.file.name,
            type: 'pdf',
            size: `${Math.round(s.file.size / 1024)} KB`,
          }
        : s.kind === 'link'
          ? { name: s.name || s.url, type: 'link', url: s.url }
          : { name: s.name, type: 'text' };
    setMockSourceList(prev => [
      { ...base, id: crypto.randomUUID(), uploadedAt: new Date() },
      ...prev,
    ]);
  };

  const handleRemoveSource = (sourceId: string) => {
    if (isApi) {
      deleteSourceMutation.mutate(sourceId);
    } else {
      setMockSourceList(prev => prev.filter(s => s.id !== sourceId));
    }
  };

  const getSourceId = (source: Source | ApiSourceItem): string => {
    return 'source_id' in source ? source.source_id : source.id;
  };

  // Collapsed view
  if (isCollapsed) {
    return (
      <SourcesPanelCollapsed
        currentVersion={currentVersion}
        sourceCount={visibleSources.length}
        activeTab={activeTab}
        onToggleCollapse={onToggleCollapse}
        onExpandWithTab={tab => {
          setActiveTab(tab);
          onToggleCollapse?.();
        }}
      />
    );
  }

  const canAddSources = isMock || isApi;

  const sourcesContent = (
    // Two-region layout: sticky Add Source header on top, scrollable list
    // below. `TabbedInterface` wraps tab content in `flex-1 overflow-hidden`,
    // so each tab's body has to manage its own scrolling.
    <div className='flex h-full min-h-0 flex-col'>
      {canAddSources &&
        visibleSources.length > 0 &&
        isViewingLatest &&
        !pipelineLocked && (
          <div className='flex-shrink-0 px-3 pb-2 pt-3'>
            <Button
              onClick={() => setAttachOpen(true)}
              className='w-full border border-gray-300 bg-muted text-foreground transition-all duration-200 hover:bg-gray-900/[0.025] dark:border-gray-600 dark:hover:bg-white/[0.04] max-sm:h-11'
              variant='outline'
            >
              <Plus className='mr-2 h-4 w-4' />
              Add Source
            </Button>
          </div>
        )}

      {/* Replaces the Add Source slot, so the user sees WHY the control is
          gone and where to go — not just an absence. */}
      {isApi && !isViewingLatest && (
        <div className='flex-shrink-0 px-3 pb-2 pt-3'>
          <PastVersionNotice
            currentVersion={apiCurrentVersion ?? currentVersion}
            latestVersion={latestVersion}
            action='add or edit sources'
          />
        </div>
      )}

      <div className='min-h-0 flex-1 overflow-y-auto p-3'>
        {isApi && apiLoading ? (
          <div className='flex h-full items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : isApi && apiError && apiSources.length === 0 ? (
          // A failed load used to fall through to "No sources yet" - telling
          // someone with three uploads that they have none, and inviting them
          // to upload again (`Layout/Lesson/SourcesPanel/SourcesFailed`).
          <PanelFallback
            variant='panel'
            title="Couldn't load your sources"
            description='Check your connection and try again.'
            primary={{
              label: 'Try again',
              onClick: () => void refetchSources(),
            }}
          />
        ) : visibleSources.length === 0 ? (
          <div className='flex h-full items-center justify-center'>
            <EmptyState
              icon={FolderOpen}
              title={
                isViewingLatest
                  ? 'No sources yet'
                  : 'No sources in this version'
              }
              description={
                isViewingLatest
                  ? 'Add PDFs, links, videos, or other materials to enhance your learning experience'
                  : 'This version was created without any sources.'
              }
              action={
                // Adding sources is disabled while the lesson is still
                // generating (pipelineLocked) - otherwise the user could stage
                // sources / create a version mid-build. Re-enabled once the
                // pipeline completes.
                isViewingLatest && canAddSources && !pipelineLocked ? (
                  <Button onClick={() => setAttachOpen(true)}>
                    <Plus className='mr-2 h-4 w-4' />
                    Add Your First Source
                  </Button>
                ) : isViewingLatest ? (
                  <Button
                    disabled
                    className='cursor-not-allowed bg-primary text-primary-foreground opacity-50'
                  >
                    <Plus className='mr-2 h-4 w-4' />
                    Add Your First Source
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <div className='space-y-2'>
            {isApi &&
              (() => {
                const rows = visibleSources.filter(
                  (s): s is ApiSourceItem => 'source_id' in s
                );
                return (
                  <SourceIngestBanner
                    total={rows.length}
                    inFlight={
                      rows.filter(
                        r => r.status === 'pending' || r.status === 'processing'
                      ).length
                    }
                    failed={rows.filter(r => r.status === 'failed').length}
                  />
                );
              })()}
            {visibleSources.map(source => {
              const sourceId = getSourceId(source);
              // On the latest version: the checkbox reflects the staged set
              // (with in-flight edits) and toggling mutates it.
              // On a non-latest version: the checkbox reflects *that version's*
              // actual source_ids - so the user can still see which sources
              // were used at the time. The checkbox is rendered in read-only
              // mode (no toggle, no hover ghost) to make the visual purpose
              // clear without inviting interaction.
              let stagedProp: boolean | undefined;
              if (isApi) {
                stagedProp = isViewingLatest
                  ? stagedSet.has(sourceId)
                  : (currentVersionRecord?.source_ids ?? []).includes(sourceId);
              } else {
                stagedProp = undefined;
              }
              return (
                <SourceItem
                  key={sourceId}
                  source={source}
                  onRemove={() => handleRemoveSource(sourceId)}
                  onView={s => {
                    // Log source view for the engagement strip. Fire-and-forget
                    // - failures must not block the source preview.
                    if (lessonId) {
                      progressApi
                        .logSourceView(lessonId, sourceId)
                        .catch(() => {});
                    }
                    onSourceSelect?.(s);
                  }}
                  staged={stagedProp}
                  stagingDisabled={stagingDisabled || pipelineLocked}
                  readOnly={isApi && locked}
                  canRetry={!sourcesAllFailed}
                  onToggleStaged={
                    isApi && isViewingLatest && !pipelineLocked
                      ? checked => toggleStaged.mutate({ sourceId, checked })
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // Context tab - now hosts the PromptOverviewPanel (replaces the legacy
  // ContextEditor). Without lessonId/lessonMetadata we render a tiny
  // placeholder rather than mounting the panel; the page-level layout
  // is responsible for resolving both before this tab is reachable on
  // a real lesson.
  const contextContent = (
    <div className='flex h-full min-h-0 flex-col p-3'>
      {lessonId && lessonMetadata ? (
        <PromptOverviewPanel
          lessonId={lessonId}
          lesson={lessonMetadata}
          overviewStatus={steps?.overview?.state ?? 'queued'}
          byteSelectionStatus={steps?.byte_selection?.state ?? 'queued'}
          isLocked={locked}
          lockReason={lockReason}
        />
      ) : (
        <PromptOverviewSkeleton />
      )}
    </div>
  );

  // StagedActionBar lives inside each tab's body (not as a sibling
  // below TabbedInterface) so it sits inside the per-sub-tab
  // PanelBoundary - when the tab body crashes, the bar is replaced by
  // the fallback too, matching the user-facing expectation that the
  // "check sources / edit the prompt" hint shouldn't show next to an
  // error.
  // Sources still ingesting or awaiting chapter selection. Creating a version
  // while any of these are in flight would snapshot the version without them
  // (they'd be stranded as a permanent staged diff), so version creation is
  // blocked until they settle - see StagedActionBar.sourcesNotReadyCount.
  const sourcesNotReadyCount = isApi
    ? (apiSources as ApiSourceItem[]).filter(
        s =>
          s.is_active &&
          (s.status === 'awaiting_scope' ||
            s.status === 'pending' ||
            s.status === 'processing')
      ).length
    : 0;

  const stagedActionBar = isApi && lessonId && (
    <StagedActionBar
      lessonId={lessonId}
      onCreated={onClose}
      lesson={lessonMetadata}
      isLocked={locked}
      lockReason={lockReason}
      latestVersion={latestVersion}
      sourcesNotReadyCount={sourcesNotReadyCount}
      activeSourceIds={
        new Set(
          (apiSources as ApiSourceItem[])
            // Exclude FAILED sources from the staged diff: a failed source
            // can never be ingested, so it shouldn't count as "1 added" or
            // prompt the user to build a version around it. (Processing
            // sources still count - they'll become ready.)
            .filter(s => s.is_active && s.status !== 'failed')
            .map(s => s.source_id)
        )
      }
    />
  );

  const sourcesTabContent = (
    <div className='flex h-full min-h-0 flex-col'>
      <div className='min-h-0 flex-1'>{sourcesContent}</div>
      {stagedActionBar}
    </div>
  );

  const contextTabContent = (
    <div className='flex h-full min-h-0 flex-col'>
      <div className='min-h-0 flex-1'>{contextContent}</div>
      {stagedActionBar}
    </div>
  );

  // While sources are still ingesting we hide the tab switcher entirely (the
  // Prompt tab has nothing to edit until the lesson starts building) and show
  // a single progress view - see `SourcesIngestingView` for why replacing the
  // tabs is right rather than disabling them.
  const ingestingView = (
    <SourcesIngestingView hasVisibleSources={visibleSources.length > 0}>
      {sourcesContent}
    </SourcesIngestingView>
  );

  // Per-sub-tab boundaries so a Sources body crash doesn't take down
  // Context (and vice versa). The `key` forces a clean remount on tab
  // switch - without it, TabbedInterface swaps PanelBoundary instances
  // at the same position and React reconciles props in-place, which
  // also skips the slide-up animation on the shared Panel* state cards.
  const tabs = [
    {
      id: 'sources',
      label: 'Sources',
      icon: FolderOpen,
      count: visibleSources.length > 0 ? visibleSources.length : undefined, // Hide badge when empty
      content: (
        <PanelBoundary key='sources' name='Sources'>
          {sourcesTabContent}
        </PanelBoundary>
      ),
    },
    {
      id: 'context',
      label: 'Prompt',
      icon: Brain,
      content: (
        <PanelBoundary key='context' name='Prompt'>
          {contextTabContent}
        </PanelBoundary>
      ),
    },
  ];

  return (
    <div className='flex h-full min-h-0 flex-col'>
      {/* Header with Back Button and Version Selector */}
      <div className='border-b border-border p-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-1'>
            {!onClose && (
              <Button
                variant='ghost'
                size='sm'
                onClick={() => navigate('/my-learning')}
                aria-label='Back to My Learning'
                className='h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
              >
                <ArrowLeft className='h-4 w-4' />
              </Button>
            )}

            <div className='relative'>
              <Button
                variant='ghost'
                size='sm'
                disabled={versionTriggerDisabled}
                onClick={() => setShowVersionSelector(v => !v)}
                title={
                  isApi && apiVersions.length <= 1
                    ? 'Only one version available'
                    : undefined
                }
                className='tap-target h-8 px-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60'
              >
                <History className='mr-1.5 h-4 w-4' />
                {displayVersion}
                <ChevronDown className='ml-1 h-3 w-3' />
              </Button>

              {showVersionSelector && (
                <VersionSelector
                  currentVersion={apiCurrentVersion ?? currentVersion}
                  versions={isApi ? apiVersions : undefined}
                  isLoading={isSwitching}
                  onVersionChange={handleVersionChange}
                  onClose={() => setShowVersionSelector(false)}
                />
              )}
            </div>
          </div>

          {onClose ? (
            <Button
              variant='ghost'
              size='sm'
              onClick={onClose}
              aria-label='Close sources'
              className='tap-target h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
            >
              <X className='h-5 w-5' />
            </Button>
          ) : (
            <Button
              variant='ghost'
              size='sm'
              onClick={onToggleCollapse}
              aria-label='Collapse sources panel'
              className='h-8 w-8 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
            >
              <Sidebar className='h-4 w-4' />
            </Button>
          )}
        </div>
      </div>

      {/* While sources ingest, the tab switcher is hidden and we show the
          single ingestion progress view. Once ingestion is terminal the
          tabs (Sources + Prompt) appear - read-only until the pipeline
          completes. Each tab.content wraps its body + the staged action bar
          in a per-sub-tab `PanelBoundary`. */}
      {sourcesIngesting ? (
        <div className='min-h-0 flex-1 overflow-hidden'>
          <PanelBoundary name='Sources'>{ingestingView}</PanelBoundary>
        </div>
      ) : (
        <TabbedInterface
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(tabId: string) =>
            setActiveTab(tabId as 'sources' | 'context')
          }
          variant='default'
          className='min-h-0 flex-1'
        />
      )}

      {/* Attach Source modal - API mode hits backend, mock mode stages locally. */}
      {isApi && lessonId ? (
        <InLessonAttachSourceModal
          isOpen={attachOpen}
          onClose={() => setAttachOpen(false)}
          lessonId={lessonId}
        />
      ) : (
        <AttachSourceModal
          isOpen={attachOpen}
          onClose={() => setAttachOpen(false)}
          onAdd={handleMockAdd}
        />
      )}
    </div>
  );
}
